import { reactive, Ref, ref } from 'vue';
import { useQuery, UseQueryOptions } from '@tanstack/vue-query';
import QUERY_KEYS from '@/constants/queryKeys';
import useNetwork from '../useNetwork';
import { getApi } from '@/dependencies/balancer-api';
import { GqlTokenPrice } from '@/services/api/graphql/generated/api-types';
import { networkToGqlChain } from '@/services/api/graphql/mappers';
import { oneMinInMs } from '../useTime';
import { getAddress } from '@ethersproject/address';

/**
 * TYPES
 */
export type TokenPrices = { [address: string]: number };
type QueryResponse = TokenPrices;
type QueryOptions = UseQueryOptions<QueryResponse>;

/**
 * Fetches token prices for all provided addresses.
 */
export default function useTokenPricesQuery(
  pricesToInject: Ref<TokenPrices> = ref({}),
  options: QueryOptions = {}
) {
  const { networkId } = useNetwork();
  const queryKey = reactive(
    QUERY_KEYS.Tokens.Prices(networkId, pricesToInject)
  );

  function priceArrayToMap(prices: GqlTokenPrice[]): TokenPrices {
    return prices.reduce(
      (obj, item) => ((obj[getAddress(item.address)] = item.price), obj),
      {}
    );
  }

  function injectCustomTokens(
    prices: TokenPrices,
    pricesToInject: TokenPrices
  ): TokenPrices {
    for (const address of Object.keys(pricesToInject)) {
      prices[address] = pricesToInject[address];
    }
    return prices;
  }

  const api = getApi();
  const queryFn = async () => {
    const chain = networkToGqlChain(networkId.value);
    const { prices } = await api.GetCurrentTokenPrices({ chains: [chain] });

    let pricesMap = priceArrayToMap(prices as GqlTokenPrice[]);
    pricesMap = injectCustomTokens(pricesMap, pricesToInject.value);
    console.log('Fetching', Object.values(prices).length, 'prices');

    return pricesMap;
  };

  const queryOptions = reactive({
    enabled: true,
    // While the query is in an error state, retry every 30s so the global
    // price alert clears soon after the provider recovers, instead of
    // waiting for the normal 5 min polling cycle.
    refetchInterval: (
      _data: QueryResponse | undefined,
      query: { state: { status: string } }
    ) => (query.state.status === 'error' ? oneMinInMs / 2 : oneMinInMs * 5),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    ...options,
  });

  return useQuery<QueryResponse>(
    queryKey,
    queryFn,
    queryOptions as QueryOptions
  );
}
